/**
 * Firebase INFRASTRUCTURE: the app, the auth instance, the tenant pin,
 * authFetch. Auth FLOWS (sign-in, MFA, session) live in modules/auth;
 * this file is the client identity plumbing every layer above may use.
 *
 * Dev/emulator: connectAuthEmulator points the SAME code path at the
 * compose stack's `firebase` service (AUTH emulator, port 9099). In
 * production builds the emulator URL is empty and the SDK talks to the
 * real project.
 */
import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';

const AUTH_PROJECT_ID: string = import.meta.env.VITE_AUTH_PROJECT_ID ?? 'demo-polywog';
// Emulator by DEFAULT only in dev builds; production must declare a real
// project (and no emulator) at build time.
export const AUTH_EMULATOR_URL: string =
  import.meta.env.VITE_AUTH_EMULATOR_URL ?? (import.meta.env.DEV ? 'http://localhost:9099' : '');

export const firebaseApp = initializeApp({
  // With the emulator the apiKey is never validated; a real project's
  // web API key (NOT a secret -- it's a public client identifier) comes
  // in via VITE_AUTH_API_KEY at build time.
  apiKey: import.meta.env.VITE_AUTH_API_KEY ?? 'demo-api-key',
  projectId: AUTH_PROJECT_ID,
  authDomain: import.meta.env.VITE_AUTH_DOMAIN ?? `${AUTH_PROJECT_ID}.firebaseapp.com`,
});

export const auth = getAuth(firebaseApp);
if (AUTH_EMULATOR_URL) {
  connectAuthEmulator(auth, AUTH_EMULATOR_URL, { disableWarnings: true });
}

// Org-first tenancy (docs/ORG_TENANCY_SPEC.md): sign-in lands in the
// org's Identity Platform tenant. Until wildcard DNS resolves tenants
// by subdomain, the FIRST org's tenant id is pinned at build time
// (empty in emulator dev -- the emulator project has no tenants).
const AUTH_TENANT_ID: string = import.meta.env.VITE_AUTH_TENANT_ID ?? '';
if (AUTH_TENANT_ID) {
  auth.tenantId = AUTH_TENANT_ID;
}

/**
 * One W3C traceparent per request: the BROWSER roots the trace and the
 * api's server span joins it as a child (FastAPI's default W3C
 * propagator reads the header). Always flagged sampled -- the head
 * sampling decision belongs to the api (TRACING_SAMPLE_RATE), and no
 * RUM SDK exists on purpose: the header is the whole client story.
 */
function traceparent(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `00-${hex.slice(0, 32)}-${hex.slice(32, 48)}-01`;
}

/**
 * fetch with the current user's ID token attached -- the ONLY way the
 * app calls the api. The SDK caches the token and refreshes it before
 * expiry; getIdToken() is cheap after first mint.
 */
export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const user = auth.currentUser;
  const headers = new Headers(init.headers);
  if (user) {
    headers.set('Authorization', `Bearer ${await user.getIdToken()}`);
  }
  headers.set('traceparent', traceparent());
  return fetch(input, { ...init, headers });
}
